// src/helpers/generalHelper.js

import { toast } from "react-toastify";
import { default_image } from "../utils/constants";

const fallbackImage = "https://via.placeholder.com/150";

export function empty(value) {
  return (
    value === undefined ||
    value === null ||
    value === false ||
    value === 0 ||
    value === "" ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === "object" &&
      !Array.isArray(value) &&
      Object.keys(value).length === 0)
  );
}

/**
 * Get the best translation object by language, with fallback to English or any other available.
 */
/**
 * Get the most appropriate translation object based on language fallback logic.
 * 1. Try given/current language
 * 2. Then try 'en'
 * 3. Then try first non-empty translation object
 */
export function getTranslation(translations, lang = null) {
  if (!translations || typeof translations !== "object") return "";

  const userLang = (lang || getUserdata("lang") || "en").toLowerCase();

  // Step 1: Try current language
  const current = translations[userLang];
  if (current && Object.values(current).some((val) => val)) {
    return current;
  }

  // Step 2: Try English
  const english = translations["en"];
  if (english && Object.values(english).some((val) => val)) {
    return english;
  }

  // Step 3: Try any non-empty translation object
  for (const translation of Object.values(translations)) {
    if (
      translation &&
      typeof translation === "object" &&
      Object.values(translation).some((val) => val)
    ) {
      return translation;
    }
  }

  // Step 4: Return empty string if nothing found
  return "";
}

/**
 * Get the best translated value for a given key across available languages.
 * Priority: current/given lang → English → first available non-empty value in any lang
 */
export function showTranslatedValue(obj, key, lang = null) {
  if (!obj || typeof obj !== "object") return "";

  const translations = obj.translations || {};
  if (!translations || typeof translations !== "object") return "";

  const normalizedLang = (lang || getUserdata("lang") || "en").toLowerCase();

  // Step 1: Try current/given language
  const currentTranslation = translations[normalizedLang];
  if (currentTranslation?.[key]) {
    return currentTranslation[key];
  }

  // Step 2: Try English
  const englishTranslation = translations["en"];
  if (englishTranslation?.[key]) {
    return englishTranslation[key];
  }

  // Step 3: Search all languages for a non-empty value
  for (const [langCode, translation] of Object.entries(translations)) {
    if (translation?.[key]) {
      return translation[key];
    }
  }

  // Step 4: Not found
  return "";
}

export function setUserdatas(datas) {
  if (!datas) return;
  localStorage.setItem("userData", JSON.stringify(datas));
  if (datas.access_token) localStorage.setItem("is_authenticated", "true");
}

export function getOrderStatusColor(statusId) {
  switch (String(statusId)) {
    case "1": // Pending
      return "bg-yellow-100 text-yellow-700 border border-yellow-200";
    case "3": // Preparing
      return "bg-orange-100 text-orange-700 border border-orange-200";
    case "4": // Ready
      return "bg-teal-100 text-teal-700 border border-teal-200";

    case "10": // Completed
      return "bg-green-100 text-green-700 border border-green-200";
    default:
      return "bg-gray-100 text-gray-700 border border-gray-200";
  }
}

// get particular key from userData object
export function getUserdatas() {
  const userData = localStorage.getItem("userData");

  if (!userData) return null;

  try {
    const parsedData = JSON.parse(userData);
    return parsedData ?? null;
  } catch (e) {
    return null;
  }
}

// get particular key from userData object
export function getUserdata(name) {
  const userData = localStorage.getItem("userData");

  if (!userData) return null;

  try {
    const parsedData = JSON.parse(userData);
    return parsedData[name] ?? null;
  } catch (e) {
    return null;
  }
}

// Set or update a specific key in userData
export function editUserdata(key, value) {
  const userData = localStorage.getItem("userData");

  let parsedData = {};
  if (userData) {
    try {
      const decoded = JSON.parse(userData);
      parsedData = (decoded && typeof decoded === 'object') ? decoded : {};
    } catch (e) {
      console.error("Failed to parse userData. Starting fresh:", e);
    }
  }

  parsedData[key] = value;

  try {
    localStorage.setItem("userData", JSON.stringify(parsedData));
  } catch (e) {
    console.error("Failed to save userData:", e);
  }
}

export function getItemFromStorage(name, is_json = true) {
  const item = localStorage.getItem(name);

  if (item === null) return null;

  if (!is_json) return item;

  try {
    return JSON.parse(item);
  } catch (e) {
    console.error(`Failed to parse JSON from localStorage item "${name}":`, e);
    return null;
  }
}

// detectLogin was removed as per user request to stop all 'automation' and 'ai scripts'
export function detectLogin() {
  // Function disabled
}


export function logoutUser(isExpired = false) {
  const isStaff = window.location.pathname.includes("/restaurant-staff");
  
  // Fire and forget backend logout to clear cookies
  try {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost/stellaris_Original/stellaris_php-main/";
    const endpoint = isStaff ? "restaurant-staff/sign-out" : "restaurant/sign-out";
    fetch(baseUrl + endpoint, { credentials: "include", method: "GET" }).catch(() => {});
  } catch(e) {}

  localStorage.clear();

  if (isExpired === true) {
    sessionStorage.setItem("logout_message_type", "expired");
  } else {
    sessionStorage.setItem("logout_message_type", "success");
  }

  window.location.href = isStaff ? "/restaurant-staff/employee/staffLogin" : "/restaurant/login";
}

export function showToast(res) {
  if (res?.success) {
    toast.success(res.message);
  } else {
    toast.error(res.message);
  }
}

// ✅ Store currency
export function setCurrency(symbol = "INR") {
  localStorage.setItem("currency", symbol);
}

// ✅ Store decimal places separately
export function setDecimalPlaces(decimalPlaces = 2) {
  const safeValue = [2, 3, 4].includes(Number(decimalPlaces))
    ? Number(decimalPlaces)
    : 2;
  localStorage.setItem("currencyDecimals", safeValue);
}

// ✅ Get both from localStorage and format
export function showCurrencySymbol(amount) {
  try {
    const currencyCode = localStorage.getItem("currency") || "INR";
    const decimalPlaces = Number(localStorage.getItem("currencyDecimals")) || 2;

    const formatter = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });

    return formatter.format(amount);
  } catch (error) {
    return `₹${amount}`;
  }
}

export function getFirstImage(menuImages) {
  return menuImages?.[0] || default_image || fallbackImage;
}

/**
 * Format time string (HH:mm or HH:mm:ss) to 12-hour format with AM/PM.
 */
export function formatTime12Hour(timeStr) {
  if (!timeStr) return "";
  
  try {
    // Split HH:mm:ss or HH:mm
    const [hours, minutes] = timeStr.split(":");
    let h = parseInt(hours, 10);
    const m = minutes;
    const ampm = h >= 12 ? "PM" : "AM";
    
    h = h % 12;
    h = h ? h : 12; // the hour '0' should be '12'
    
    return `${h}:${m} ${ampm}`;
  } catch (error) {
    return timeStr;
  }
}
